import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TenantDataService } from 'app/config/services/tenant-data.service';
import { SelectMxImageLibraryDialogComponent } from 'app/library/image-library/dialogs/select-mx-image/select-mx-image.component';
import { UnsubscribeOnDestroy } from 'app/projects/core/src/lib/models/unsubscribe-on-destroy';
import { MAXBRAIN_ENVIRONMENT } from 'app/projects/core/src/lib/services/environment.token';
import { MaxBrainEditorDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/editor/editor.component';
import { IMaxBrainEnvironment } from 'app/projects/shared/src/lib/interfaces/environment.interface';
import { ILanguageService } from 'app/projects/shared/src/lib/interfaces/language.interface';
import { MAXBRAIN_LANGUAGE_SERVICE } from 'app/projects/shared/src/lib/services/language.token';
import { TinymceService } from 'app/services/tinymce.service';
import * as moment from 'moment';
import { filter } from 'rxjs/operators';
import { MaxbrainImage } from '../../../../image-library/models/maxbrain-image.model';
import { PostingType } from '../../../models/posting-type.model';
import { Posting } from '../../../models/posting.model';
import { PostingTypeApiService } from '../../../services/posting-type.api-service';

@Component({
    selector: 'app-engage-new-posting-dialog',
    templateUrl: './engage-new-posting-dialog.component.html',
    styleUrls: ['./engage-new-posting-dialog.component.scss'],
})
export class EngageNewPostingDialogComponent extends UnsubscribeOnDestroy implements OnInit {
    // Dialog to open Mx Image Library
    imageLibraryDialogRef: MatDialogRef<SelectMxImageLibraryDialogComponent>;

    // image Selected with MxImageLib
    maxBrainImageSelected: MaxbrainImage;

    @Input()
    postingData: Posting;

    @Input('postingType')
    postingType: PostingType;

    @Input('postingFromArticle')
    postingFromArticle: boolean = false;

    @Input()
    isEditMode: boolean = false;

    // Create Angular Form
    private _formGroup: FormGroup;

    public controls = null;

    unsplashViewsBaseUrl: string;

    get formGroup(): FormGroup {
        return this._formGroup;
    }

    get formTypes(): PostingType[] {
        return this._postingTypes;
    }

    get articleId() {
        return this._formGroup.get('articleId');
    }

    private _postingTypes: PostingType[];

    minDate: Date;
    editorDialogRef: MatDialogRef<MaxBrainEditorDialogComponent>;
    textEditorCharacterLimit = 600;
    defaultLanguage: string;

    updateEndDate(): void {
        const startDate = this._formGroup.get('validFrom').value;

        this.minDate = startDate ? new Date(startDate) : null;
    }

    constructor(
        @Inject(MAXBRAIN_ENVIRONMENT) private _environment: IMaxBrainEnvironment,
        @Inject(MAXBRAIN_LANGUAGE_SERVICE) public languageService: ILanguageService,
        public dialogRef: MatDialogRef<EngageNewPostingDialogComponent>,
        private _formBuilder: FormBuilder,
        private _matDialog: MatDialog,
        private _postingTypeApiService: PostingTypeApiService,
        private _tinymceService: TinymceService,
        private _tenantDataService: TenantDataService
    ) {
        super();

        this.defaultLanguage = this._tenantDataService.defaultLanguage;
        this.unsplashViewsBaseUrl = this._environment.unsplashViewsBaseUrl;
        this.postingData = new Posting();
    }

    async ngOnInit(): Promise<void> {
        if (this.postingData.image) {
            this.maxBrainImageSelected = this.postingData.image;
        }

        // MAX-6516 - If we are creating new posting, we should preselect posting type
        let postingTypeId: string = this.postingType ? this.postingType.id : this.postingData.postingType.id;

        const urlReg =
            /((([A-Za-z]{3,9}:(?:\/\/)?)(?:[\-;:&=\+\$,\w]+@)?[A-Za-z0-9\.\-]+|(?:www\.|[\-;:&=\+\$,\w]+@)[A-Za-z0-9\.\-]+)((?:\/[\+~%\/\.\w\-_]*)?\??(?:[\-\+=&;%@\.\w_]*)#?(?:[\.\!\/\\\w]*))?)/;

        this._formGroup = this._formBuilder.group({
            postingType: this._formBuilder.group({
                type: new FormControl(postingTypeId, Validators.required),
            }),
            translations: this._formBuilder.group({
                title: new FormControl(this.postingData.translations[0].title, [Validators.required, Validators.maxLength(150)]), // todo: change translations in translation
                description: new FormControl(this.postingData.translations[0].description || '', Validators.required), // todo: change translations in translation
                language: new FormControl(this.postingData.translations[0].language || this.defaultLanguage, Validators.required),
            }),
            url: new FormControl({ value: this.postingData.url, disabled: this.postingFromArticle }, Validators.pattern(urlReg)),
            img: new FormControl(this.postingData.image, Validators.required),
            validFrom: new FormControl(this.postingData.validFrom || moment(), Validators.required),
            validTo: new FormControl(this.postingData.validTo || moment('2099-12-31')),
            kind: new FormControl(this.postingData.kind),
        });

        if (this.postingFromArticle) {
            this._formGroup.addControl('articleId', new FormControl(this.postingData.id, Validators.required));
        }

        this.controls = {
            type: this._formGroup.get('postingType.name'),
            title: this._formGroup.get('translations.title'),
            description: this._formGroup.get('translations.description'),
            language: this._formGroup.get('translations.language'),
            url: this._formGroup.get('url'),
            img: this._formGroup.get('img'),
            validFrom: this._formGroup.get('validFrom'),
            validTo: this._formGroup.get('validFrom'),
            kind: this._formGroup.get('kind'),
        };

        this._postingTypes = await this._postingTypeApiService.getPostingTypes();
    }

    createPosting(): void {
        const formRawData = this._formGroup.getRawValue();
        const posting = new Posting(null, formRawData);

        // load data from Posting Type
        const postingTypeRaw = this._postingTypes.find((postType) => postType.id === posting.postingType.id);
        posting.postingType = new PostingType(null, postingTypeRaw);
        // load image
        posting.image = this.maxBrainImageSelected;

        this.dialogRef.close(posting);
    }

    public openImageLibraryDialog(): void {
        this.imageLibraryDialogRef = this._matDialog.open(SelectMxImageLibraryDialogComponent, {
            width: '1000px',
            minHeight: 'calc(100vh - 230px)',
            height: 'auto',
            disableClose: true,
        });

        // Keep the same 420:240 aspect ratio, but export higher resolution for sharper images.
        this.imageLibraryDialogRef.componentInstance.previewImageHeight = 720;
        this.imageLibraryDialogRef.componentInstance.previewImageWidth = 1260;

        this.imageLibraryDialogRef.afterClosed().subscribe((image: MaxbrainImage) => {
            if (image) {
                this.maxBrainImageSelected = new MaxbrainImage(null, image);
                this._formGroup.get('img').setValue(this.maxBrainImageSelected);
                this.formGroup.markAsDirty();
            }
        });
    }

    editDescription(): void {
        this.editorDialogRef = this._matDialog.open(MaxBrainEditorDialogComponent, {
            width: '1000px',
            disableClose: true,
        });

        const init: any = this._tinymceService.getInitConfiguration();
        init.plugins = `link`;

        const minNumberOfRows = 6;
        init.min_height = minNumberOfRows * 32 + 20;

        this.editorDialogRef.componentInstance.dialogTitle = 'FORM.EVENT.FIELD.DESCRIPTION';
        this.editorDialogRef.componentInstance.init = init;
        this.editorDialogRef.componentInstance.data = this.formGroup.get('translations.description').value;
        this.editorDialogRef.componentInstance.icCountingCharacters = true;
        this.editorDialogRef.componentInstance.characterLimit = this.textEditorCharacterLimit;

        this.editorDialogRef
            .afterClosed()
            .pipe(filter((result) => !!result))
            .subscribe((result) => {
                if (result.data !== this.formGroup.get('translations.description')) {
                    this.formGroup.get('translations').patchValue({
                        description: result.data,
                    });
                    this.formGroup.markAsDirty();
                }
            });
    }
}
